
import { HeroSection } from "@/components/blocks/hero-section-dark";
import { Navbar } from "@/components/blocks/Navbar";
import { Logos3 } from "@/components/blocks/logos3";
import { Feature } from "@/components/blocks/feature-with-image";
import { Testimonial } from "@/components/blocks/testimonial";
import { BackgroundBeamsDemo } from "@/components/blocks/background-beams-demo";
import { Faq3 } from "@/components/blocks/faq3";
import { Footer } from "@/components/blocks/footer";
import { RevealOnScroll } from "@/components/ui/reveal-on-scroll";
import { HeroVideoDialog } from "@/components/ui/hero-video-dialog";
import { TabsDemo } from "@/components/blocks/tabs-demo";
import quickatooLogoFooter from "@/assets/quickatoo-logo-footer.png";
import dashboardThumb from "@/assets/quickatoo-dashboard-thumbnail.png";
import { Seo } from "@/components/Seo";


const faqData = {
  heading: "Frequently asked questions",
  description: "Everything you need to know about Quickatoo. Can't find the answer you're looking for? Feel free to contact our support team.",
  items: [{
    id: "faq-1",
    question: "How does Quickatoo calculate material costs?",
    answer: "Quickatoo uses an advanced estimating algorithm that factors in surface area, primer and topcoat coverage, spread rates, labor efficiency, and additional materials like sundries. You can use industry-standard pricing or customize it based on your preferred suppliers and job-specific requirements."
  }, {
    id: "faq-2",
    question: "Can I customise my quotes with my company branding?",
    answer: "Absolutely! Quickatoo lets you add your company logo, business name, contact details, and personalised terms and conditions to your quotes, ensuring a professional and consistent brand image for your clients."
  }, {
    id: "faq-3",
    question: "Can I integrate Quickatoo with my accounting software?",
    answer: "Yes! We are currently developing integrations for Xero and MYOB to streamline your invoicing and financial management. Stay tuned for updates as we expand compatibility with accounting platforms."
  }, {
    id: "faq-4",
    question: "What types of painting jobs does Quickatoo support?",
    answer: "Quickatoo is built to handle a wide range of painting projects, including: Residential (interior & exterior), Commercial & industrial projects, Special finishes (Venetian plaster, decorative coatings), Decking and protective coatings, Maintenance and repaints."
  }, {
    id: "faq-5",
    question: "How accurate are the measurements taken through Quickatoo?",
    answer: "Quickatoo provides highly accurate measurements by using industry-standard formulas for surface areas, paint coverage, and application rates. Users can input custom dimensions and adjust variables to ensure real-world precision."
  }, {
    id: "faq-6",
    question: "Is there a limit to how many quotes I can create?",
    answer: "We have plans that fit your needs, so you only pay for what you use—no wasted services."
  }, {
    id: "faq-7",
    question: "Can multiple team members use the same account?",
    answer: "Yes! Quickatoo supports team collaboration, allowing multiple users to access the same account with role-based permissions. Whether you're an estimator, project manager, or painter, everyone stays connected and aligned."
  }, {
    id: "faq-8",
    question: "How secure is my customer data in Quickatoo?",
    answer: "We take data security seriously. Quickatoo uses end-to-end encryption, secure cloud storage, and regular backups to keep your customer data protected and confidential."
  }],
  supportHeading: "Still have questions?",
  supportDescription: "Can't find the answer you're looking for? Our support team is here to help with any technical questions or concerns.",
  supportButtonText: "Contact Support",
  supportButtonUrl: "mailto:hi@quickatoo.app"
};

const footerData = {
  logo: <img src={quickatooLogoFooter} alt="Quickatoo Painters Estimator" className="h-20 w-auto md:h-24" />,
  brandName: "Quickatoo",
  socialLinks: [],
  mainLinks: [{
    href: "/",
    label: "Home"
  }, {
    href: "#why-quickatoo",
    label: "Why Quickatoo"
  }, {
    href: "#about",
    label: "About"
  }, {
    href: "#faq",
    label: "FAQ"
  }, {
    href: "#contact",
    label: "Contact"
  }],
  legalLinks: [{
    href: "/privacy",
    label: "Privacy"
  }, {
    href: "/terms",
    label: "Terms"
  }],
  copyright: {
    text: "© 2026 Quickatoo",
    license: "All rights reserved"
  }
};

const Index = () => {
  return <>
      <Seo
        title="Quickatoo — Painting Quotes & Job Management"
        description="Quickatoo helps painting contractors create accurate quotes in minutes, manage jobs and grow their business. Formerly PaintAPP."
        path="/"
      />
      <Navbar />

      <main className="pt-16 overflow-hidden w-full">
        <HeroSection
          title="The quickest way to quote, plan and manage any painting job."
          intro={[
            "Level up your painting business.",
            "Quickatoo gives you total control to deliver professional, accurate, and detailed quotes in minutes - not hours."
          ]}
          punchline="Win more jobs & get more done."
          ctaText="Unlock Your Success"
          ctaHref="https://app.quickatoo.com/auth/login?view=sign_up"
        >
          <div className="mt-16 mx-auto px-4 max-w-4xl relative z-10">
            <HeroVideoDialog
              animationStyle="from-center"
              videoSrc="https://www.youtube.com/embed/xcJtKu3eMhY"
              thumbnailSrc={dashboardThumb}
              thumbnailAlt="Quickatoo dashboard overview demo video"
            />
          </div>
        </HeroSection>
        
        <RevealOnScroll direction="up" distance={120} delay={100}>
          <Logos3 />
        </RevealOnScroll>
        
        <div id="why-quickatoo">
          <Feature />
        </div>
        
        <div id="about">
          <RevealOnScroll direction="up" distance={150} delay={300}>
            <TabsDemo />
          </RevealOnScroll>
        </div>

        <RevealOnScroll direction="left" distance={150} delay={200}>
          <Testimonial
            quote="At Quickatoo, our promise is to provide a smarter way to quote, plan, track, and manage your projects. By streamlining your workflow, we help you achieve brighter results and grow your business more effectively."
            secondaryQuote="Quickatoo is designed to lead to better workdays, bigger margins, and a business that practically runs itself."
            highlightedText="Quickatoo"
          />
        </RevealOnScroll>
        
        <BackgroundBeamsDemo />
        
        <div id="faq">
          <RevealOnScroll direction="right" distance={120} delay={300}>
            <Faq3 {...faqData} />
          </RevealOnScroll>
        </div>
        
        <div id="contact">
          <RevealOnScroll direction="up" distance={100} delay={200}>
            <Footer {...footerData} />
          </RevealOnScroll>
        </div>
      </main>
    </>;
};
export default Index;
